import { format } from "date-fns";
import { capitalize } from "lodash-es";
import { AlertCircle, CalendarIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { DialogBody, DialogFooter } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { HttpError, isBadRequest } from "@/types/error";
import {
  PublicOrganisationTrial,
  ReplayTypeEnum,
  TrialFeature,
} from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";

import {
  getOrganisationQueryKey,
  useGetOrganisation,
} from "../../organisations/api/get-organisation";
import { getTrialsQueryKey } from "../api/fetch-trials";
import { useStartTrial } from "../api/start-trial";
import {
  StartTrialFormData,
  startTrialSchema,
} from "../types/start-trial-schema";

type StartTrialFormProps = {
  handleClose: () => void;
};

export function StartTrialForm(props: StartTrialFormProps) {
  const { handleClose } = props;
  const { id } = useParams();
  const organisationId = id ?? "";
  const { data, isSuccess: isOrganisationSuccess } = useGetOrganisation({
    id: organisationId,
  });
  const queryClient = useQueryClient();

  const replayVersionOptions: { label: string; value: ReplayTypeEnum }[] = [];

  if (isOrganisationSuccess && data?.license?.replay === ReplayTypeEnum.LITE) {
    replayVersionOptions.push(
      ...[
        { label: "Replay", value: ReplayTypeEnum.REPLAY },
        { label: "Advanced", value: ReplayTypeEnum.ADVANCED },
      ],
    );
  }

  if (
    isOrganisationSuccess &&
    data?.license?.replay === ReplayTypeEnum.REPLAY
  ) {
    replayVersionOptions.push(
      ...[{ label: "Advanced", value: ReplayTypeEnum.ADVANCED }],
    );
  }

  const form = useForm<StartTrialFormData>({
    resolver: zodResolver(startTrialSchema),
    defaultValues: {
      newReplayVersion:
        replayVersionOptions[0]?.value ?? ReplayTypeEnum.ADVANCED,
      date: {
        from: new Date(),
        to: new Date(),
      },
    },
  });

  const { mutateAsync: startTrial } = useStartTrial({
    onSuccess: async (data) => {
      //optimistically update the trials list
      queryClient.setQueryData(
        getTrialsQueryKey(organisationId),
        (oldData: PublicOrganisationTrial[]) => {
          if (oldData.length === 0) {
            return [data];
          }
          return [...oldData, data];
        },
      );

      await queryClient.refetchQueries({
        queryKey: getOrganisationQueryKey(organisationId),
      });

      handleClose();
    },
  });

  async function handleStartTrial(data: StartTrialFormData) {
    try {
      await startTrial({
        organisationId,
        trial: {
          feature: TrialFeature.REPLAY,
          startDate: data.date.from.toISOString(),
          endDate: data.date.to.toISOString(),
          replayVersion: data.newReplayVersion,
        },
      });
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  const currentReplayVersion = isOrganisationSuccess
    ? capitalize(data?.license?.replay)
    : "";

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit(handleStartTrial)(e);
        }}
      >
        <DialogBody className="z-10 overflow-auto">
          <div className="mb-4">
            <Label>Current Replay Version</Label>
            <Input
              autoFocus
              autoComplete="off"
              value={currentReplayVersion}
              disabled
            />
          </div>
          <FormField
            name="newReplayVersion"
            render={({ field }) => (
              <FormItem className="mb-4">
                <FormLabel isRequired>Trial Replay Version</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                  disabled={form.formState.isSubmitting}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a replay version..." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {replayVersionOptions.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        className="capitalize"
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Trial Period</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-[240px] pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground",
                        )}
                      >
                        {field.value ? (
                          field.value.to ? (
                            <>
                              {format(field.value.from, "PP")} -{" "}
                              {format(field.value.to, "PP")}
                            </>
                          ) : (
                            format(field.value.from, "PP")
                          )
                        ) : (
                          <span>Select a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      selected={field.value}
                      onSelect={field.onChange}
                      disabled={(date) =>
                        date < new Date(new Date().setHours(0, 0, 0, 0))
                      }
                      captionLayout="dropdown"
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
          {form.formState.errors.root && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Unexpected error</AlertTitle>
              <AlertDescription>
                {form.formState.errors.root.message}
              </AlertDescription>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            type="button"
            onClick={handleClose}
            disabled={form.formState.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            type="submit"
            disabled={form.formState.isSubmitting}
          >
            Start Trial
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
